"use client";

/**
 * Interface language. English is the product's language and stays the
 * default everywhere; Ukrainian is opt-in, chosen in Account → Language
 * (and from the Language menu in the desktop app).
 *
 * The keys ARE the English strings. That is deliberate: a string nobody
 * has translated yet still renders — in English — instead of showing a
 * key, so the UI can never end up half-broken, and adding a language is
 * only ever additive. It also keeps call sites readable: `t("Delete
 * project")` says what it draws.
 *
 * `t` is a plain module-level function rather than a hook, which is what
 * makes this retrofit safe across ~25 existing files: a call site needs an
 * import and nothing else — no hook to place correctly inside a component
 * body, no rule-of-hooks hazard in callbacks, and it works inside event
 * handlers and `useMemo` alike. Re-rendering on a language change is
 * handled by LanguageProvider remounting its subtree (see below), which is
 * the right trade for a setting someone changes once.
 *
 * Module-level constants (NAV_ITEMS, WORKSPACES, …) keep their English
 * labels and are translated where they are rendered — `t(w.label)` — so
 * none of those arrays had to change, and none of them may call `t`
 * themselves: at import time the language is not known yet.
 */

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { uk } from "@/lib/locales/uk";

export type Lang = "en" | "uk";

export const LANGUAGES: { code: Lang; label: string; english: string }[] = [
  { code: "en", label: "English", english: "English" },
  { code: "uk", label: "Українська", english: "Ukrainian" },
];

/** Shared with the desktop shell's Language menu, which writes the same
 *  key before reloading the window. */
export const LANG_STORAGE_KEY = "neodub_lang";

const DICTIONARIES: Record<Lang, Record<string, string>> = { en: {}, uk };

let currentLang: Lang = "en";

export type Vars = Record<string, string | number>;

/** `t("Shared with {count} accounts", { count: 3 })` */
export function t(key: string, vars?: Vars): string {
  let out = DICTIONARIES[currentLang][key] ?? key;
  if (vars) {
    for (const [name, value] of Object.entries(vars)) {
      out = out.split(`{${name}}`).join(String(value));
    }
  }
  return out;
}

/**
 * Ukrainian needs three forms where English needs two (1 файл / 2-4 файли /
 * 5 файлів), and the rule is not derivable from the English plural — so
 * the few places that count things ask for all three.
 */
export function plural(count: number, forms: { one: string; few: string; many: string }): string {
  if (currentLang !== "uk") return count === 1 ? forms.one : forms.many;
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return forms.one;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return forms.few;
  return forms.many;
}

export function readStoredLang(): Lang {
  try {
    const stored = window.localStorage.getItem(LANG_STORAGE_KEY);
    return stored === "uk" || stored === "en" ? stored : "en";
  } catch {
    return "en";
  }
}

interface LanguageValue {
  lang: Lang;
  setLang: (lang: Lang) => void;
}

const LanguageContext = createContext<LanguageValue>({ lang: "en", setLang: () => {} });

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  // Always starts English so the first client render matches the HTML the
  // build prerendered — reading storage during render instead would be a
  // hydration mismatch on every translated string. A Ukrainian session
  // shows English for the one frame before this effect runs.
  const [lang, setLangState] = useState<Lang>("en");

  useEffect(() => {
    const stored = readStoredLang();
    if (stored !== "en") {
      currentLang = stored;
      setLangState(stored);
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  const setLang = useCallback((next: Lang) => {
    currentLang = next;
    setLangState(next);
    try {
      window.localStorage.setItem(LANG_STORAGE_KEY, next);
    } catch {
      // Private mode / storage disabled — the choice just does not persist.
    }
  }, []);

  const value = useMemo(() => ({ lang, setLang }), [lang, setLang]);

  // The key is what makes the switch take effect: `t` is read during
  // render and subscribes to nothing, so the subtree is remounted rather
  // than relying on every component happening to re-render. Costs a
  // refetch of what is on screen, once, on a deliberate action.
  return (
    <LanguageContext.Provider value={value}>
      <div key={lang} className="contents">
        {children}
      </div>
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
